import { ChevronLeft, ChevronRight, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { AnnualBudget } from '@/hooks/use-annual-budget';

interface BudgetHeaderProps {
  year: number;
  onYearChange: (year: number) => void;
  budget: AnnualBudget | null;
  totalIncome: number;
  totalExpenses: number;
  totalActual: number;
  onAddItem: () => void;
}

export function BudgetHeader({
  year,
  onYearChange,
  budget,
  totalIncome,
  totalExpenses,
  totalActual,
  onAddItem,
}: BudgetHeaderProps) {
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const balance = totalIncome - totalExpenses;
  const balanceColor = balance >= 0 ? 'text-emerald-500' : 'text-red-500';

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="icon"
            onClick={() => onYearChange(year - 1)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-xl sm:text-2xl font-bold">{year}</h1>
          <Button
            variant="outline"
            size="icon"
            onClick={() => onYearChange(year + 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button size="sm" onClick={onAddItem}>
          <Plus className="h-4 w-4 sm:mr-2" />
          <span className="hidden sm:inline">Novo Item</span>
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-card rounded-lg p-4 border">
          <p className="text-sm text-muted-foreground">Receita Planejada</p>
          <p className="text-base sm:text-xl font-semibold text-emerald-500">
            {formatCurrency(totalIncome)}
          </p>
        </div>
        <div className="bg-card rounded-lg p-4 border">
          <p className="text-sm text-muted-foreground">Despesa Planejada</p>
          <p className="text-base sm:text-xl font-semibold text-amber-500">
            {formatCurrency(totalExpenses)}
          </p>
        </div>
        <div className="bg-card rounded-lg p-4 border">
          <p className="text-sm text-muted-foreground">Gasto Real</p>
          <p className="text-base sm:text-xl font-semibold text-red-500">
            {formatCurrency(totalActual)}
          </p>
        </div>
        <div className="bg-card rounded-lg p-4 border">
          <p className="text-sm text-muted-foreground">Saldo Previsto</p>
          <p className={`text-base sm:text-xl font-semibold ${balanceColor}`}>
            {formatCurrency(balance)}
          </p>
        </div>
      </div>
    </div>
  );
}
